import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Calendar, MessageCircle, TrendingUp, Zap, AlertCircle, ArrowRight, Clock, Flame, DollarSign, RefreshCw, Plus, Users, Bot } from "lucide-react";
import { Button } from "../components/ui/button";
import { LeadsBySourceWidget } from "./SourceBadge";
import DemoChoiceModal from "./DemoChoiceModal";
import "./demo2.css";

const API = process.env.REACT_APP_BACKEND_URL || "";

function useToday(closerUsername, refreshTick) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);

  const fetchData = async () => {
    try {
      const qs = closerUsername ? `?closer_username=${closerUsername}` : "";
      const r = await fetch(`${API}/api/demo/today${qs}`);
      const j = await r.json();
      setData(j);
      setUpdatedAt(new Date());
    } catch (e) { /* keep previous */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 30000);
    return () => clearInterval(t);
  }, [closerUsername, refreshTick]);

  return { data, loading, updatedAt, refresh: fetchData };
}

function timeUntil(minutes) {
  if (minutes == null) return "";
  const abs = Math.abs(minutes);
  if (minutes < -1) return `${Math.floor(abs / 60)}h ${abs % 60}m ago`;
  if (minutes < 1) return "now";
  if (minutes < 60) return `in ${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h < 24 ? `in ${h}h ${m}m` : `in ${Math.floor(h / 24)}d`;
}

export default function Today() {
  const { auth } = useOutletContext();
  const navigate = useNavigate();
  const isAdmin = auth?.username === "admin";
  const closerUsername = isAdmin ? null : auth?.username;

  const { data, loading, updatedAt, refresh } = useToday(closerUsername);
  const [demoChoiceLead, setDemoChoiceLead] = useState(null);
  const stats = data?.stats || {};
  const demosToday = data?.demosToday || [];
  const unreplied = data?.unreplied || [];
  const hotLeads = data?.hotLeads || [];
  const upcoming = data?.upcomingWeek || [];
  const automation = data?.automation || {};
  const health = data?.health || "green";

  const healthColor = health === "green" ? "#10b981" : health === "yellow" ? "#f59e0b" : "#ef4444";
  const healthLabel = health === "green" ? "All systems healthy" : health === "yellow" ? "Minor issues — check automation" : "Issues detected";

  // Next best action: pick the most urgent item
  const nextAction = (() => {
    const urgentDemo = demosToday.find(d => d.minutesUntil != null && d.minutesUntil > -5 && d.minutesUntil < 120);
    if (urgentDemo) {
      const label = urgentDemo.minutesUntil < 10 ? "STARTING NOW" : `Demo ${timeUntil(urgentDemo.minutesUntil)}`;
      return { type: "demo", label, lead: urgentDemo, icon: <Zap className="w-5 h-5" /> };
    }
    if (unreplied.length > 0) {
      return { type: "reply", label: `${unreplied.length} unreplied ${unreplied.length === 1 ? "message" : "messages"}`, lead: unreplied[0], icon: <MessageCircle className="w-5 h-5" /> };
    }
    if (hotLeads.length > 0) {
      return { type: "hot", label: "Close a demo_done lead", lead: hotLeads[0], icon: <Flame className="w-5 h-5" /> };
    }
    return null;
  })();

  return (
    <div className="today-root" data-testid="today-root">
      {/* ─── Greeting + health ─── */}
      <div className="today-topbar">
        <div className="today-greeting">
          <h1 data-testid="today-greeting">
            {timeOfDay()}, {auth?.name || "there"}
          </h1>
          <div className="today-role-chip">{isAdmin ? "Admin" : "Closer"}</div>
        </div>
        <div className="today-topbar-actions">
          <div className="today-health" style={{ color: healthColor }} data-testid="today-health" title={healthLabel}>
            <span className="today-health-dot" style={{ background: healthColor }} />
            <span className="today-health-label">{healthLabel}</span>
          </div>
        </div>
      </div>

      {/* ─── Next Best Action strip ─── */}
      {nextAction ? (
        <div className={`today-nba today-nba-${nextAction.type}`} data-testid="today-nba">
          <div className="today-nba-icon">{nextAction.icon}</div>
          <div className="today-nba-text">
            <span className="today-nba-tag">NEXT BEST ACTION</span>
            <span className="today-nba-label">{nextAction.label}</span>
            <span className="today-nba-lead">
              {nextAction.lead.clientName} {nextAction.lead.clientLastName || ""}
              {nextAction.lead.targetRole && <span className="today-nba-role"> · {nextAction.lead.targetRole}</span>}
            </span>
          </div>
          <Button
            size="sm"
            onClick={() => {
              if (nextAction.type === "demo") setDemoChoiceLead(nextAction.lead);
              else if (nextAction.type === "reply") navigate(`/demo/inbox/${nextAction.lead.id}`);
              else navigate(`/demo/lead/${nextAction.lead.id}`);
            }}
            data-testid="today-nba-action"
          >
            {nextAction.type === "demo" ? "Open demo" : "Open chat"} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      ) : (
        <div className="today-nba today-nba-quiet" data-testid="today-nba-empty">
          <Zap className="w-5 h-5" style={{ color: "#10b981" }} />
          <span>You're all caught up. No urgent actions right now.</span>
        </div>
      )}

      {/* ─── Stat cards (role-aware) ─── */}
      <div className="today-stats">
        {isAdmin && (
          <>
            <StatCard icon={<DollarSign className="w-4 h-4" />} label="Revenue today" value={`$${(stats.revenueToday || 0).toLocaleString()}`} accent="#10b981" testId="stat-revenue-today" />
            <StatCard icon={<DollarSign className="w-4 h-4" />} label="Revenue this week" value={`$${(stats.revenueWeek || 0).toLocaleString()}`} accent="#10b981" testId="stat-revenue-week" />
            <StatCard icon={<DollarSign className="w-4 h-4" />} label="Revenue this month" value={`$${(stats.revenueMTD || 0).toLocaleString()}`} accent="#10b981" testId="stat-revenue-month" />
            <StatCard icon={<TrendingUp className="w-4 h-4" />} label="Converted MTD" value={stats.convertedMTD || 0} accent="#7F77DD" />
          </>
        )}
        <StatCard icon={<Users className="w-4 h-4" />} label="New leads today" value={stats.newLeadsToday || 0} accent="#3b82f6" />
        <StatCard icon={<Calendar className="w-4 h-4" />} label="Demos today" value={demosToday.length} accent="#f59e0b" />
        <StatCard icon={<MessageCircle className="w-4 h-4" />} label="Unreplied" value={stats.unrepliedCount || 0} accent={stats.unrepliedCount > 0 ? "#ef4444" : "#9ca3af"} />
        <StatCard icon={<Flame className="w-4 h-4" />} label="Pipeline" value={stats.pipelineCount || 0} accent="#EC4899" />
      </div>

      {/* ─── Recent revenue (admin only) ─── */}
      {isAdmin && (data?.recentConversions?.length || 0) > 0 && (
        <div className="today-recent-revenue" data-testid="today-recent-revenue">
          <div className="today-card-header" style={{ borderBottom: "1px solid #e5e7eb", paddingBottom: 8, marginBottom: 10 }}>
            <h3><DollarSign className="w-4 h-4" /> Recent revenue <span className="today-source-tag">demo_leads · paidAt &lt; 30d</span></h3>
            <span className="today-card-count">{data.recentConversions.length}</span>
          </div>
          <div className="today-recent-revenue-list">
            {data.recentConversions.map(c => {
              const amt = c.paidAmount ?? c.convertedAmount ?? 0;
              const planLabel = (c.selectedPlan || c.recommendedPlan || "—").replace(/-/g, " ").replace(/\b\w/g, x => x.toUpperCase());
              return (
                <div key={c.id} className="today-row today-revenue-row" data-testid={`recent-rev-${c.id}`} onClick={() => navigate(`/demo/lead/${c.id}`)}>
                  <div className="today-row-avatar" style={{ background: hashColor(c.clientName || "") }}>{initials(c.clientName)}</div>
                  <div className="today-row-info">
                    <div className="today-row-name">{c.clientName} {c.clientLastName || ""}</div>
                    <div className="today-row-sub">{planLabel} · {c.closerName || "—"} · {relativeTime(c.paidAt || c.convertedAt)}</div>
                  </div>
                  <div className="today-revenue-amount" style={{ color: "#10b981", fontWeight: 700 }}>${Number(amt).toLocaleString()}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── Leads by source (ad attribution) ─── */}
      <div style={{ marginBottom: 20 }}>
        <LeadsBySourceWidget apiBase={API} />
      </div>

      {/* ─── 3-column grid ─── */}
      <div className="today-grid">
        {/* Demos today */}
        <div className="today-card" data-testid="today-demos-card">
          <div className="today-card-header">
            <h3><Calendar className="w-4 h-4" /> Demos today <span className="today-source-tag">scheduledAt = today (IST)</span></h3>
            <span className="today-card-count">{demosToday.length}</span>
          </div>
          <div className="today-card-body">
            {demosToday.length === 0 ? (
              <EmptyState icon={<Calendar className="w-6 h-6" />} text="No demos today" sub={upcoming.length > 0 ? `Next: ${upcoming[0].clientName} ${formatShort(upcoming[0].scheduledAt)}` : "Quiet day — good time to reach out to warm leads"} />
            ) : demosToday.map(d => (
              <div key={d.id} className="today-row today-demo-row" data-testid={`demo-row-${d.id}`} onClick={() => setDemoChoiceLead(d)}>
                <div className="today-row-avatar" style={{ background: hashColor(d.clientName || "") }}>
                  {initials(d.clientName)}
                </div>
                <div className="today-row-info">
                  <div className="today-row-name">{d.clientName} {d.clientLastName || ""}</div>
                  <div className="today-row-sub">
                    {d.display?.ist_short} · {d.targetRole || "—"}
                  </div>
                </div>
                <div className={`today-demo-timer ${d.minutesUntil != null && d.minutesUntil < 30 ? "urgent" : ""}`}>
                  {timeUntil(d.minutesUntil)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Unreplied inbox */}
        <div className="today-card today-card-wide" data-testid="today-inbox-card">
          <div className="today-card-header">
            <h3><MessageCircle className="w-4 h-4" /> Unreplied messages <span className="today-source-tag">whatsapp_messages · unread &gt; 0</span></h3>
            <span className="today-card-count">{unreplied.length}</span>
          </div>
          <div className="today-card-body">
            {unreplied.length === 0 ? (
              <EmptyState icon={<MessageCircle className="w-6 h-6" />} text="Inbox clean" sub="Every lead has been responded to — ship it" />
            ) : unreplied.map(u => (
              <div key={u.id} className="today-row today-inbox-row" data-testid={`inbox-row-${u.id}`} onClick={() => navigate(`/demo/inbox/${u.id}`)}>
                <div className="today-row-avatar" style={{ background: hashColor(u.clientName || "") }}>
                  {initials(u.clientName)}
                </div>
                <div className="today-row-info">
                  <div className="today-row-name">
                    {u.clientName} {u.clientLastName || ""}
                    <span className="today-inbox-badge">{u.waUnreadCount}</span>
                  </div>
                  <div className="today-row-sub">
                    {u.whatsappNumber} · {relativeTime(u.lastWaMessageAt)}
                  </div>
                </div>
                <div className="today-status-chip">{prettyStatus(u.status)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Hot leads + automation health */}
        <div className="today-card-col">
          <div className="today-card">
            <div className="today-card-header">
              <h3><Flame className="w-4 h-4" /> Hot leads <span className="today-source-tag">demo_leads · score ≥ 70</span></h3>
              <span className="today-card-count">{hotLeads.length}</span>
            </div>
            <div className="today-card-body">
              {hotLeads.length === 0 ? (
                <EmptyState icon={<Flame className="w-6 h-6" />} text="No hot leads" sub="Demo-done leads waiting to close will appear here" />
              ) : hotLeads.map(h => (
                <div key={h.id} className="today-row today-hot-row" onClick={() => navigate(`/demo/lead/${h.id}`)} data-testid={`hot-row-${h.id}`}>
                  <div className="today-row-avatar hot" style={{ background: hashColor(h.clientName || "") }}>
                    {initials(h.clientName)}
                  </div>
                  <div className="today-row-info">
                    <div className="today-row-name">{h.clientName}</div>
                    <div className="today-row-sub">{prettyPlan(h.recommendedPlan)} · {relativeTime(h.demoDoneAt)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {isAdmin && (
            <div className="today-card">
              <div className="today-card-header">
                <h3><Zap className="w-4 h-4" /> Automation (24h)</h3>
                <span className="today-health-dot" style={{ background: healthColor }} />
              </div>
              <div className="today-card-body today-automation">
                <AutoRow label="Reminders sent" value={automation.reminders_24h} />
                <AutoRow label="Messages in / out" value={`${automation.messages_in_24h || 0} / ${automation.messages_out_24h || 0}`} />
                <AutoRow label="CAPI events" value={`${automation.capi_events_success_24h || 0} / ${automation.capi_events_24h || 0}`}
                  warn={(automation.capi_events_24h || 0) > 0 && (automation.capi_events_success_24h || 0) < (automation.capi_events_24h || 0)} />
                <AutoRow label="Auto no-shows" value={automation.auto_no_shows_24h} />
              </div>
            </div>
          )}
        </div>
      </div>

      {updatedAt && (
        <div className="today-footer">Last updated {updatedAt.toLocaleTimeString()} · auto-refreshes every 30s</div>
      )}
      {demoChoiceLead && (
        <DemoChoiceModal lead={demoChoiceLead} onClose={() => setDemoChoiceLead(null)} />
      )}
    </div>
  );
}

function StatCard({ icon, label, value, accent, testId }) {
  return (
    <div className="today-stat" data-testid={testId || `stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="today-stat-icon" style={{ background: accent + "15", color: accent }}>{icon}</div>
      <div className="today-stat-text">
        <span className="today-stat-value">{value}</span>
        <span className="today-stat-label">{label}</span>
      </div>
    </div>
  );
}

function EmptyState({ icon, text, sub }) {
  return (
    <div className="today-empty">
      <div className="today-empty-icon">{icon}</div>
      <div className="today-empty-text">{text}</div>
      {sub && <div className="today-empty-sub">{sub}</div>}
    </div>
  );
}

function AutoRow({ label, value, warn }) {
  return (
    <div className={`today-auto-row ${warn ? "warn" : ""}`}>
      <span>{label}</span>
      <strong>{value ?? 0}</strong>
    </div>
  );
}

// ─── Helpers ───
function timeOfDay() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}
function initials(n) {
  if (!n) return "?";
  const parts = n.trim().split(" ");
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}
function hashColor(str) {
  const colors = ["#7F77DD", "#EC4899", "#f59e0b", "#10b981", "#3b82f6", "#D85A30"];
  let h = 0;
  for (const c of str) h = (h * 31 + c.charCodeAt(0)) & 0xfffffff;
  return colors[h % colors.length];
}
function relativeTime(iso) {
  if (!iso) return "";
  const diff = (new Date() - new Date(iso)) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
function formatShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
function prettyStatus(s) {
  const map = { new: "new", upcoming: "upcoming", demo_done: "demo done", demo_in_progress: "in progress", payment_sent: "payment sent", converted: "won", cold: "cold", no_show: "no-show" };
  return map[s] || s;
}
function prettyPlan(p) {
  if (!p) return "—";
  return p.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}
